/**
 * @file app.component.ts
 * @version 2.4.3
 * @datetime 2026-09-09T03:55:00+03:00
 * @description Main application component managing navigation, side menu, CouchDB live users/taxpayers, and module switches.
 */

import { Component, inject, signal, computed, effect, OnInit } from '@angular/core';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import {
  IonApp,
  IonSplitPane,
  IonMenu,
  IonContent,
  IonList,
  IonListHeader,
  IonMenuToggle,
  IonItem,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonRouterLink,
  IonBadge,
  IonSegment,
  IonSegmentButton,
  IonSearchbar,
} from '@ionic/angular';
import { addIcons } from 'ionicons';
import {
  chatbubbleEllipsesOutline,
  chatbubbleEllipsesSharp,
  peopleOutline,
  peopleSharp,
  cubeOutline,
  cubeSharp,
  documentTextOutline,
  documentTextSharp,
  statsChartOutline,
  statsChartSharp,
  serverOutline,
  serverSharp,
  cloudDoneOutline,
  cloudDoneSharp,
  cloudOfflineOutline,
  cloudOfflineSharp,
  syncOutline,
  syncSharp,
  receiptOutline,
  receiptSharp,
  folderOutline,
  folderSharp,
  calendarOutline,
  calendarSharp,
  calculatorOutline,
  calculatorSharp,
  logInOutline,
  logInSharp,
  logOutOutline,
  logOutSharp,
  personCircleOutline,
  businessOutline,
  businessSharp,
  callOutline,
  callSharp,
  checkmarkCircle,
} from 'ionicons/icons';
import { DatabaseService, MukellefItem } from './services/database.service';
import { GoogleWorkspaceService } from './services/google-workspace.service';
import { AuthService } from './services/auth.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    IonApp,
    IonSplitPane,
    IonMenu,
    IonContent,
    IonList,
    IonListHeader,
    IonMenuToggle,
    IonItem,
    IonIcon,
    IonLabel,
    IonRouterLink,
    IonRouterOutlet,
    IonBadge,
    IonSegment,
    IonSegmentButton,
    IonSearchbar,
  ],
})
export class AppComponent implements OnInit {
  readonly db = inject(DatabaseService);
  readonly googleWorkspace = inject(GoogleWorkspaceService);
  readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  // Side Menu Top Tab: 'moduller' vs 'mukellefler'
  readonly sideMenuTab = signal<'moduller' | 'mukellefler'>('moduller');
  readonly mukellefSearch = signal<string>('');
  readonly activePanel = signal<string>('sohbet');

  protected readonly appPages = [
    { title: 'Sohbet', panel: 'sohbet', url: '/main', queryParams: { panel: 'sohbet' }, icon: 'chatbubble-ellipses' },
    { title: 'Fatura', panel: 'fatura', url: '/main', queryParams: { panel: 'fatura' }, icon: 'receipt' },
    { title: 'Dosyalar', panel: 'dosyalar', url: '/main', queryParams: { panel: 'dosyalar' }, icon: 'folder' },
    { title: 'Takvim', panel: 'takvim', url: '/main', queryParams: { panel: 'takvim' }, icon: 'calendar' },
    { title: 'Müşteriler', panel: 'musteriler', url: '/main', queryParams: { panel: 'musteriler' }, icon: 'people' },
    { title: 'Ürünler', panel: 'urunler', url: '/main', queryParams: { panel: 'urunler' }, icon: 'cube' },
    { title: 'Evraklar', panel: 'evraklar', url: '/main', queryParams: { panel: 'evraklar' }, icon: 'document-text' },
    { title: 'Rapor', panel: 'rapor', url: '/main', queryParams: { panel: 'rapor' }, icon: 'stats-chart' },
    { title: 'Hesap', panel: 'hesap', url: '/main', queryParams: { panel: 'hesap' }, icon: 'calculator' },
  ];

  readonly filteredMukellefler = computed(() => {
    const q = this.mukellefSearch().trim().toLowerCase();
    const user = this.auth.currentUser();
    const selfVkn = user?.vkn || user?.vknTckn || user?.VKN || user?.taxid || this.db.rootVkn() || '';

    let list = this.db.mukellefler();

    // Fallback if list is not yet loaded from server/PouchDB
    if (list.length === 0 && selfVkn) {
      list = [{
        name: `${user?.name || user?.organization || 'SMMM Ofisi'} (Kendisi)`,
        vkn: selfVkn,
        phone: user?.phone || '',
        email: '',
        isSelf: true,
      }];
    }

    if (!q) return list;
    return list.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.vkn.includes(q) ||
        (m.phone && m.phone.toLowerCase().includes(q))
    );
  });

  protected readonly databases = [
    { name: 'daily_currency_rates', label: 'Döviz Kurları DB' },
    { name: 'muhasebe', label: 'Muhasebe DB' },
  ];

  async ngOnInit(): Promise<void> {
    const root = this.db.rootVkn() || this.auth.currentUser()?.vkn || '';
    if (root) {
      await this.db.fetchMukellefler(root);
    }
  }

  constructor() {
    addIcons({
      chatbubbleEllipsesOutline,
      chatbubbleEllipsesSharp,
      peopleOutline,
      peopleSharp,
      cubeOutline,
      cubeSharp,
      documentTextOutline,
      documentTextSharp,
      statsChartOutline,
      statsChartSharp,
      serverOutline,
      serverSharp,
      cloudDoneOutline,
      cloudDoneSharp,
      cloudOfflineOutline,
      cloudOfflineSharp,
      syncOutline,
      syncSharp,
      receiptOutline,
      receiptSharp,
      folderOutline,
      folderSharp,
      calendarOutline,
      calendarSharp,
      calculatorOutline,
      calculatorSharp,
      logInOutline,
      logInSharp,
      logOutOutline,
      logOutSharp,
      personCircleOutline,
      businessOutline,
      businessSharp,
      callOutline,
      callSharp,
      checkmarkCircle,
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        try {
          const tree = this.router.parseUrl(event.urlAfterRedirects || event.url);
          const panel = tree.queryParams['panel'] || (tree.root.children['primary']?.segments[0]?.path === 'main' ? 'sohbet' : '');
          if (panel) {
            this.activePanel.set(panel);
          }
        } catch {
          // ignore parse errors
        }
      });
  }

  getBadgeCount(panel: string): string | number | null {
    switch (panel) {
      case 'sohbet':
        return this.db.chatMessages().length;
      case 'fatura':
        return this.db.invoices().length;
      case 'dosyalar':
        return this.db.originalDocs().length;
      case 'takvim':
        return this.googleWorkspace.calendarEvents().length;
      case 'musteriler':
        return this.db.customers().length;
      case 'urunler':
        return this.db.products().length;
      case 'evraklar':
        return this.db.invoices().length;
      case 'hesap':
        return this.db.tokenUsages().length;
      case 'rapor':
        return 'Özet';
      default:
        return null;
    }
  }

  navigateToPanel(panel: string): void {
    this.activePanel.set(panel);
    this.router.navigate(['/main'], { queryParams: { panel } });
  }

  selectMukellef(m: MukellefItem): void {
    this.db.switchVkn(m.vkn);
    console.log(`[Ustat] Active Mükellef switched to: ${m.name} (VKN: ${m.vkn})`);
  }
}


